import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip, Select as AntSelect, Spin } from 'antd'
import { faAdd } from '@fortawesome/pro-solid-svg-icons/faAdd'
import { faTrash } from '@fortawesome/pro-solid-svg-icons/faTrash'
import { faGraduationCap } from '@fortawesome/pro-solid-svg-icons/faGraduationCap'
import { RankmiTable } from '@rankmi/rankmi-table'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import EmptyState from 'components/reusables/emptyState'
import Select from 'components/reusables/select'
import { ILearningProgram } from 'interfaces/roles'
import { learningPathsService } from 'api/administration/learningPaths'
import { LearningPathStatus } from 'enum/learningPathStatus.enum'
import { emptyLearningProgram } from 'store/slices/initialData'
import { useRolesAndPermissions } from '../../../../../context/RolesAndPermissionsContext'
import { Wrapper, Options, AddLearningPaths } from './styled'

const { Option } = AntSelect

interface IProps {
  selectedLearningPrograms: ILearningProgram[]
  setSelectedLearningPrograms: (value: ILearningProgram[]) => void
  onViewCourses: (program: ILearningProgram) => void
}

const LearningPrograms = ({
  selectedLearningPrograms,
  setSelectedLearningPrograms,
  onViewCourses,
}: IProps) => {
  const { t } = useTranslation()
  const prefix =
    'administration.learningPaths.manage.create.content.prerequisites'
  const [learningPrograms, setLearningPrograms] = useState<ILearningProgram[]>(
    []
  )
  const [loading, setLoading] = useState(false)
  const [hasEmptyLearningPath, setHasEmptyLearningPath] = useState(false)
  const { roleDetail: data } = useRolesAndPermissions()

  useEffect(() => {
    getLearningPrograms()
  }, [])

  useEffect(() => {
    setHasEmptyLearningPath(false)
    selectedLearningPrograms.forEach((item) => {
      if (item.id === 0) {
        setHasEmptyLearningPath(true)
      }
    })
  }, [selectedLearningPrograms])

  const getLearningPrograms = async () => {
    setLoading(true)
    await learningPathsService.getLearningPaths().then((response) => {
      setLearningPrograms(
        response.map((program) => ({
          id: program.id,
          name: program.name,
          state: program.state,
          courses: program.totalCourses,
        }))
      )
    })
    setLoading(false)
  }

  const seeDetail = (value: number) => {
    const program = selectedLearningPrograms.find((item) => item.id === value)
    if (program) {
      onViewCourses(program)
    }
  }

  const removeLearningProgram = (id: number) => {
    const newSelectedLearningPrograms = selectedLearningPrograms.filter(
      (item) => item.id !== id
    )
    setSelectedLearningPrograms(
      newSelectedLearningPrograms.length
        ? newSelectedLearningPrograms
        : [emptyLearningProgram]
    )
  }

  const getAvailablePrograms = (currentId: number) => {
    const selectedIds = selectedLearningPrograms
      .filter((item) => item.id !== currentId && item.id !== 0)
      .map((item) => item.id)

    const alreadyAddedIds = data.learningPrograms.map((item) => item.id)

    return learningPrograms.filter((program) => {
      const isPublished = program.state === LearningPathStatus.published
      const isCurrentSelection = program.id === currentId
      const isNotSelectedElsewhere = !selectedIds.includes(program.id)
      const isNotAlreadyAdded = !alreadyAddedIds.includes(program.id)

      return (
        isPublished &&
        (isCurrentSelection || (isNotSelectedElsewhere && isNotAlreadyAdded))
      )
    })
  }

  const locale = {
    header: {
      rowsCount: t(`${prefix}.rowsCount`),
      rowsCount_plural: t(`${prefix}.rowsCountPlural`),
    },
  }

  const columns = [
    {
      title: t(`${prefix}.columns.selectLearningPath.title`),
      key: 'id',
      render: (id: number) => {
        const availablePrograms = getAvailablePrograms(id)

        return (
          <Spin spinning={loading}>
            <Select
              showSearch={true}
              value={id || undefined}
              placeholder={t(
                `${prefix}.columns.selectLearningPath.placeholder`
              )}
              onSelect={(value) => {
                setSelectedLearningPrograms(
                  selectedLearningPrograms.map((item) => {
                    if (item.id === id) {
                      return learningPrograms.find(
                        (learningProgram) => learningProgram.id === value
                      ) as ILearningProgram
                    }
                    return item
                  })
                )
              }}
              notFoundContent={
                <EmptyState
                  title={t(`${prefix}.columns.selectLearningPath.emptyState`)}
                  icon={faGraduationCap}
                />
              }
            >
              {availablePrograms.map((item) => (
                <Option key={item.id} value={item.id} label={item.name}>
                  {item.name}
                </Option>
              ))}
            </Select>
          </Spin>
        )
      },
    },
    {
      width: 150,
      key: 'id',
      render: (id: number) => (
        <Options>
          <Button onClick={() => seeDetail(id)} disabled={id === 0}>
            {t(
              'configuration.rolesAndPermissions.form.addLearningPrograms.columns.options.seeCourses'
            )}
          </Button>
          <Tooltip
            title={t(`${prefix}.columns.options.remove`)}
            placement="right"
          >
            <Button type="link" onClick={() => removeLearningProgram(id)}>
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Tooltip>
        </Options>
      ),
    },
  ]

  return (
    <Wrapper>
      <RankmiTable
        locale={locale}
        columnHeaders={columns}
        data={selectedLearningPrograms}
        total={selectedLearningPrograms.length - (hasEmptyLearningPath ? 1 : 0)}
        countIcon={faGraduationCap}
      />
      <AddLearningPaths>
        <Button
          type="link"
          onClick={() =>
            setSelectedLearningPrograms([
              ...selectedLearningPrograms,
              emptyLearningProgram,
            ])
          }
          disabled={hasEmptyLearningPath}
        >
          <FontAwesomeIcon icon={faAdd} />
          {t(`${prefix}.buttons.addLearningPaths`)}
        </Button>
      </AddLearningPaths>
    </Wrapper>
  )
}

export default LearningPrograms
